import { atom, useLatest, useStateXAtomSetter } from '@cloudio-saas/statex';
import { Input, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useEffect, useRef, useState } from 'react';
import { PubSub } from 'utils/PubSub';

const INPUT_HEIGHT = 88;

const useStyles = makeStyles((theme: Theme) => ({
  input: {
    border: 0,
    borderTop: `1px solid ${theme.palette.divider}`,
    padding: 0,
    '& textarea': {
      backgroundColor: theme.palette.background.paper,
      resize: 'none',
      height: INPUT_HEIGHT - 1,
      padding: '8px 8px',
      boxSizing: 'border-box',
    },
  },
  textAreaWrapper: {},
}));

const findNextParam = (expr: string, start: number) => {
  let st = -1;
  let en = -1;
  for (let i = start; i < expr.length; i++) {
    if (!st && expr.charAt(i) === ')') {
      en = -1;
    } else if (st !== -1 && expr.charAt(i) === '(') {
      st = i;
    } else if (st === -1 && expr.charAt(i) === ',') {
      st = i;
    } else if (st !== -1 && expr.charAt(i) === ',') {
      en = i;
      break;
    } else if (st !== -1 && expr.charAt(i) === ')') {
      en = i;
      break;
    }
  }
  if (st === -1 || en === -1) {
    return { st: start, en: start };
  }
  return { st, en };
};

export const insertAtCaretAtom = atom({
  path: ['transient', 'exp', 'insertAtText'],
  defaultValue: '',
});

export const expAtom = atom({
  path: ['transient', 'exp', 'value'],
  defaultValue: '',
});

export interface EventMap {
  insertAt: string;
  clear: undefined;
}

export default function ExpressionInput({
  value: _value,
  pubsub,
}: {
  pubsub: PubSub<EventMap>;
  value: string;
}) {
  const [value, setValue] = useState(_value);
  const latestValue = useLatest(value);
  const ref = useRef<HTMLTextAreaElement>(null);
  const classes = useStyles();
  const setExp = useStateXAtomSetter(expAtom);
  const [startEnd, setStartEnd] = useState({
    start: 0,
    end: 0,
  });

  useEffect(() => {
    pubsub.sub('clear', async () => {
      const ta = ref.current;
      setValue('');
      setExp('');
      if (!ta) return;
      ta.focus();
    });

    pubsub.sub('insertAt', async (key, textVal?: string) => {
      const ta = ref.current;
      if (!(ta && textVal)) return;
      const lValue = latestValue.current;

      const start = ta.selectionStart;
      const end = ta.selectionEnd;
      const newExp =
        lValue.substring(0, start) + textVal + lValue.substring(end);

      setValue(newExp);
      setExp(newExp);

      if (
        textVal.indexOf('(') > 0 &&
        textVal.indexOf(')') > textVal.indexOf('(') + 1
      ) {
        setStartEnd({
          start: start + textVal.indexOf('(') + 1,
          end:
            start +
            (textVal.indexOf(',') > 0
              ? textVal.indexOf(',')
              : textVal.length - 1),
        });
      } else {
        let index = textVal.indexOf('VARIABLE_NAME');
        if (index === -1) {
          index = textVal.indexOf('PARAM_NAME');
        }
        if (index !== -1) {
          setStartEnd({
            start: start + index,
            end: start + textVal.length,
          });
        } else {
          const { st, en } = findNextParam(newExp, start + textVal.length - 1);
          setStartEnd({
            start: st + 1,
            end: st === en ? en + 1 : en,
          });
        }
      }
    });
  }, [latestValue, pubsub, setExp]);

  useEffect(() => {
    const ta = ref.current;
    if (!ta) return;
    ta.selectionStart = startEnd.start;
    ta.selectionEnd = startEnd.end;
    ta.focus();
  }, [ref, startEnd]);

  useEffect(() => {
    const ta = ref.current;
    if (!ta) return;
    ta.focus();
    ta.select();
  }, [ref]);

  useEffect(() => {
    setValue(_value);
    setExp(_value);
  }, [_value, setExp]);

  return (
    <div className={classes.textAreaWrapper}>
      <Input
        className={classes.input}
        spellCheck={false}
        autoComplete="off"
        autoCorrect="off"
        multiline
        rows={3}
        onChange={(e) => {
          setValue(e.target.value);
          setExp(e.target.value);
        }}
        placeholder="Expression..."
        value={value ?? ''}
        inputRef={ref}
        fullWidth
        disableUnderline
      />
    </div>
  );
}
